/**
 * types.ts — shared types for the @marco/pdf builder.
 *
 * InformeVM is the normalized view model consumed by all sub-builders.
 * Theme mirrors the shape from apps/web/reports/pdf/brand.ts so the
 * same Marca → Theme mapping works in both runtimes.
 */

export type InformeType = "IE" | "IS" | "AC";

// ─── Theme ───────────────────────────────────────────────────────────────────

/**
 * Theme — brand palette + contact data injected from the company Marca.
 * Colors are hex strings (#rrggbb). Missing/invalid fields fall back to
 * the MARCO defaults so the document always has a readable palette.
 */
export interface Theme {
  primary: string;
  accent: string;
  contacto: {
    web: string;
    correo: string;
    direccion: string;
    telefonos: string;
  };
  nombre: string;
  tagline: string;
  /** Logo for DARK surfaces (cover, header band). Usually a light/white logo. */
  logoUrl: string;
  /** Logo for LIGHT surfaces (closing signature, light header). Usually a dark logo. */
  logoUrlClaro: string;
}

export const DEFAULT_THEME: Theme = {
  primary: "#14385d",
  accent: "#29b6e8",
  contacto: { web: "", correo: "", direccion: "", telefonos: "" },
  nombre: "",
  tagline: "",
  logoUrl: "",
  logoUrlClaro: "",
};

/** Subset of the Marca admin type relevant for theme building. */
export interface MarcaTheme {
  colorPrimario?: string;
  colorAcento?: string;
  sitioWeb?: string;
  correo?: string;
  direccion?: string;
  telefonos?: string;
  nombreComercial?: string;
  tagline?: string;
  logoUrl?: string;
  logoUrlClaro?: string;
}

function isHex6(s: unknown): s is string {
  return typeof s === "string" && /^#[0-9a-fA-F]{6}$/.test(s);
}

/** Build a Theme from a Marca, falling back to the default MARCO palette per field. */
export function themeFromMarca(m?: MarcaTheme | null): Theme {
  return {
    primary: isHex6(m?.colorPrimario) ? m!.colorPrimario! : DEFAULT_THEME.primary,
    accent: isHex6(m?.colorAcento) ? m!.colorAcento! : DEFAULT_THEME.accent,
    contacto: {
      web: (m?.sitioWeb ?? "").trim(),
      correo: (m?.correo ?? "").trim(),
      direccion: (m?.direccion ?? "").trim(),
      telefonos: (m?.telefonos ?? "").trim(),
    },
    nombre: (m?.nombreComercial ?? "").trim(),
    tagline: (m?.tagline ?? "").trim(),
    logoUrl: (m?.logoUrl ?? "").trim(),
    logoUrlClaro: (m?.logoUrlClaro ?? "").trim(),
  };
}

// ─── InformeVM ───────────────────────────────────────────────────────────────

export interface VmDatosGenerales {
  cliente: string;
  numero: string;
  atencion: string;
  cargo: string;
  emision: string;
  supervisor: string;
  anexo: string;
  revision: string;
  fechaInicio: string;
  fechaCierre: string;
  referencia: string;
  lugar: string;
  ocCliente: string;
  otMpsa: string;
}

export interface VmEquipo {
  item: number;
  equipo: string;
  marca: string;
  modelo: string;
  serie: string;
  designacion: string;
}

export interface VmFoto {
  /** Data URI (jpeg/png) or empty string for gray placeholder. */
  dataUri: string;
  titulo: string;
  descripcion: string;
}

export interface VmActividad {
  descripcion: string;
  fotoUri?: string;
}

export interface VmProblema {
  descripcion: string;
  recomendacion: string;
}

/** Column definition for generic engrase table. */
export interface VmTablaColumna {
  id: string;
  titulo: string;
}

export interface VmTablaGenerica {
  columnas: VmTablaColumna[];
  filas: Record<string, string>[];
}

export interface VmZonaEngrase {
  titulo: string;
  descripcion: string;
  tabla: VmTablaGenerica;
}

export interface VmEngrase {
  tiempoReposo: string;
  tiempoLubricacion: string;
  tipoPresion: string;
  presionTrabajo: string;
  presionLubricacion: string;
  zonas: VmZonaEngrase[];
}

export interface VmFirmas {
  tecnico: string;
  cliente?: string;
}

/**
 * InformeVM — normalized view model consumed by all pdf sub-builders.
 *
 * Adapters (browser / node) are responsible for mapping the raw informe
 * entity into this shape BEFORE calling buildInformeDoc.
 *
 * All photo URLs are pre-resolved to data URIs so the builder stays pure
 * (no async, no fetch, no DOM).
 */
export interface InformeVM {
  tipo: InformeType;
  numero: string;
  datos: VmDatosGenerales;
  equipos: VmEquipo[];
  actividades: VmActividad[];
  fotos: VmFoto[];
  /** IE only — problemas field */
  problemas?: VmProblema[];
  /** IE only — numbered list labels for §4 and §5 */
  problemasLabel?: string;
  /** IS/AC — conclusiones y recomendaciones */
  conclusiones?: string[];
  /** IS only — conditional engrase block */
  tipoServicio?: "engrase" | "otro";
  engrase?: VmEngrase;
  /** AC only */
  firmas?: VmFirmas;
}
